import { useAsync } from '@nxsys/hooks/useAsync';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';
import {
  GetReversalPayrollsParams,
  GetReversalPayrollsResponse,
  PerformReversalPayrollsParams,
  PerformReversalPayrollsResponse,
  GetTimesheetByIdParams,
  GetTimesheetByIdResponse,
  CancelTimesheetParams,
  CancelTimesheetResponse,
  ValidateDraftPayrollParams,
  ValidateDraftPayrollResponse,
  GenerateDraftPayrollParams,
  GenerateDraftPayrollResponse,
  GenerateInvoiceParams,
  GenerateInvoiceResponse,
  CreateTimesheetResponse,
  CreateTimesheetParams,
  UpdateTimesheetResponse,
  UpdateTimesheetParams,
  ChangeTaxPeriodResponse,
  ChangeTaxPeriodParams,
  BulkChangeTaxPeriodResponse,
  BulkChangeTaxPeriodParams,
  GetTimesheetImportsResponse,
  GetTimesheetImportsParams,
  GetTimesheetGroupingResponse,
  GetTimesheetGroupingParams,
  ApproveTimesheetResponse,
  ApproveTimesheetParams,
  SaveRunPayrollResponse,
  SaveRunPayrollParams,
  CancelSalaryResponse,
  CancelSalaryParams,
} from '@nxsys/types/api';
import {
  getReversalPayrollsApi,
  performReversalPayrollsApi,
  getTimesheetByIdApi,
  cancelTimesheetApi,
  validateDraftPayrollApi,
  generateDraftPayrollApi,
  generateInvoiceApi,
  createTimesheetApi,
  updateTimesheetApi,
  changeTaxPeriodApi,
  bulkChangeTaxPeriodApi,
  getTimesheetImportsApi,
  getTimesheetGroupingApi,
  approveTimesheetApi,
  getBureauTimesheetGroupingApi,
  saveRunPayrollApi,
  cancelSalaryApi,
} from '@nxsys/services/api';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

export const useGetReversalPayrolls = () => {
  const { value, loading, error, run } = useAsync<
    GetReversalPayrollsResponse,
    GetReversalPayrollsParams
  >(getReversalPayrollsApi);

  const _run = (params: GetReversalPayrollsParams) =>
    run({
      ...params,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

// NX-5237 (#9): fetch the import files available to filter the listing by.
export const useGetTimesheetImports = () => {
  const { value, loading, error, run } = useAsync<
    GetTimesheetImportsResponse,
    GetTimesheetImportsParams
  >(getTimesheetImportsApi);

  const _run = (params: GetTimesheetImportsParams) => run({ ...params });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const usePerformReversalPayrolls = () => {
  const { value, loading, error, run } = useAsync<
    PerformReversalPayrollsResponse,
    PerformReversalPayrollsParams
  >(performReversalPayrollsApi);

  const _run = (params: PerformReversalPayrollsParams) =>
    run({
      ...params,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetTimesheetById = () => {
  const { value, loading, error, run } = useAsync<
    GetTimesheetByIdResponse,
    GetTimesheetByIdParams
  >(getTimesheetByIdApi);

  const _run = (params: GetTimesheetByIdParams) =>
    run({
      ...params,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCancelTimesheet = () => {
  const { value, loading, error, run } = useAsync<
    CancelTimesheetResponse,
    CancelTimesheetParams
  >(cancelTimesheetApi);

  const _run = (params: CancelTimesheetParams) => run(params);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useValidateDraftPayroll = () => {
  const { isBureau } = useSelector((state: AppState) => state.appStore);
  const { value, loading, error, run } = useAsync<
    ValidateDraftPayrollResponse,
    ValidateDraftPayrollParams
  >((params) => validateDraftPayrollApi(params, isBureau));

  const _run = (params: ValidateDraftPayrollParams) =>
    run({
      ...params,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGenerateDraftPayroll = () => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const { value, loading, error, run } = useAsync<
    GenerateDraftPayrollResponse,
    GenerateDraftPayrollParams
  >((params) => generateDraftPayrollApi(params, isIreland));

  const _run = (params: Omit<GenerateDraftPayrollParams, 'isIreland'>) =>
    run({
      ...params,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGenerateInvoice = () => {
  const { value, loading, error, run } = useAsync<
    GenerateInvoiceResponse,
    GenerateInvoiceParams
  >(generateInvoiceApi);

  const _run = (params: GenerateInvoiceParams) =>
    run({
      ...params,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useCreateTimesheet = () => {
  const { value, loading, error, run } = useAsync<
    CreateTimesheetResponse,
    CreateTimesheetParams
  >(createTimesheetApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUpdateTimesheetById = () => {
  const { value, loading, error, run } = useAsync<
    UpdateTimesheetResponse,
    UpdateTimesheetParams
  >(updateTimesheetApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useChangeTaxPeriod = () => {
  const { value, loading, error, run } = useAsync<
    ChangeTaxPeriodResponse,
    ChangeTaxPeriodParams
  >(changeTaxPeriodApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useBulkChangeTaxPeriod = () => {
  const { value, loading, error, run } = useAsync<
    BulkChangeTaxPeriodResponse,
    BulkChangeTaxPeriodParams
  >(bulkChangeTaxPeriodApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetTimesheetGrouping = () => {
  const { value, loading, error, run } = useAsync<
    GetTimesheetGroupingResponse,
    GetTimesheetGroupingParams
  >(getTimesheetGroupingApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetBureauTimesheetGrouping = () => {
  const { user, isBureauEmployer } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { value, loading, error, run } = useAsync<
    GetTimesheetGroupingResponse,
    GetTimesheetGroupingParams
  >((params) =>
    getBureauTimesheetGroupingApi({
      ...params,
      // After params, so a bureau employer's selection can only narrow their own companies
      companyRefs: resolveFilterCompanyIds(
        params?.companyRefs,
        isBureauEmployer,
        user?.companyUserIds,
      ),
      ...(isBureauEmployer ? { noFilterByApprover: true } : {}),
    }),
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useApproveTimesheet = () => {
  const { value, loading, error, run } = useAsync<
    ApproveTimesheetResponse,
    ApproveTimesheetParams
  >(approveTimesheetApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSaveRunPayroll = () => {
  const { value, loading, error, run } = useAsync<
    SaveRunPayrollResponse,
    SaveRunPayrollParams
  >(saveRunPayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useCancelSalary = () => {
  const { value, loading, error, run } = useAsync<
    CancelSalaryResponse,
    CancelSalaryParams
  >(cancelSalaryApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};
